import type { Category, Todo } from "../../domain/entities.ts";
import { bindBoardDragging } from "./board-drag-controller.ts";
import { transitionedCompletion } from "./board-configuration.ts";
import { activeTags } from "./selectors.ts";

export interface BoardReorderCapabilities<Snapshot, Field extends string> {
  editable(): boolean;
  tasks(): Todo[];
  categories(): Category[];
  categoryId(): string | null;
  rootId(): string | null;
  mutate(id: string, update: (todo: Todo) => void): Promise<unknown>;
  loadTask(id: string): Promise<Todo | undefined>;
  createNextOccurrence(todo: Todo): Promise<unknown>;
  render(full?: boolean): Promise<void>;
  wipExceeded(todo: Todo): boolean;
  snapshot(todo: Todo): Snapshot;
  taskUndo(message: string, rootId: string, snapshots: Snapshot[], fields: Field[]): void;
  actionUndo(message: string, undo: () => Promise<void>): void;
  sync(): void;
  hapticFeedback(): void;
  reducedMotion(): boolean;
  now(): number;
  ensureVisible?(collectionId: string, column: string, index: number): void;
  highlightMoved?(id: string): void;
}

export function boardTagTransition(todo: Todo, category: Category | undefined, boardTag: string | null): { previousColumn: string | null; tagOrder: string[]; tags: Record<string, boolean> } {
  const tags = activeTags(todo);
  const previousColumn = category?.boardColumns?.find(name => name === tags[0]) || null;
  const remaining = previousColumn ? tags.slice(1) : tags;
  const tagOrder = boardTag ? [boardTag, ...remaining.filter(tag => tag !== boardTag)] : remaining;
  return { previousColumn, tagOrder, tags: Object.fromEntries(tagOrder.map(tag => [tag, true])) };
}

export function createBoardReorderWorkflowController<Snapshot, Field extends string>(capabilities: BoardReorderCapabilities<Snapshot, Field>) {
  return {
    async moveToColumn(id: string, nextColumn: string | null): Promise<void> {
      if (!capabilities.editable()) return;
      const todo = capabilities.tasks().find(item => item.id === id);
      const category = capabilities.categories().find(item => item.id === capabilities.categoryId());
      if (!todo || !category || todo.categoryId !== category.id || (nextColumn !== null && !category.boardColumns?.includes(nextColumn))) return;
      const transition = boardTagTransition(todo, category, nextColumn);
      if (transition.previousColumn === nextColumn) return;
      const before = { position: todo.position, tagOrder: [...(todo.tagOrder || [])], tags: { ...(todo.tags || {}) }, completed: todo.completed, completedAt: todo.completedAt };
      const destination = capabilities.tasks().filter(item => item.id !== id && item.categoryId === category.id && (category.boardColumns?.includes(activeTags(item)[0]) ? activeTags(item)[0] : null) === nextColumn);
      const position = Math.max(-1, ...destination.map(item => item.position)) + 1;
      await capabilities.mutate(id, item => {
        item.position = position;
        item.tagOrder = transition.tagOrder;
        item.tags = transition.tags;
        item.completed = transitionedCompletion(item.completed, transition.previousColumn ? category.boardColumnSettings?.[transition.previousColumn] : undefined, nextColumn ? category.boardColumnSettings?.[nextColumn] : undefined);
        item.completedAt = item.completed ? item.completedAt || capabilities.now() : null;
      });
      const movedBeforeRender = await capabilities.loadTask(id);
      if (!before.completed && movedBeforeRender?.completed) await capabilities.createNextOccurrence(movedBeforeRender);
      capabilities.ensureVisible?.(category.id, nextColumn || "Unassigned", destination.length);
      await capabilities.render(false);
      capabilities.highlightMoved?.(id);
      const moved = capabilities.tasks().find(item => item.id === id);
      const exceeded = !!moved && capabilities.wipExceeded(moved);
      capabilities.actionUndo(`Placed in ${nextColumn || "Unassigned"}${exceeded ? " · WIP limit exceeded" : ""}`, async () => {
        await capabilities.mutate(id, item => { item.position = before.position; item.tagOrder = before.tagOrder; item.tags = before.tags; item.completed = before.completed; item.completedAt = before.completedAt; });
      });
      capabilities.sync();
    },
    bind(root: ParentNode): void {
      if (!capabilities.editable()) return;
      bindBoardDragging(root, {
        snapshots: visibleIds => capabilities.tasks().filter(todo => visibleIds.has(todo.id)).map(todo => ({ id: todo.id, epicId: todo.epicId || null, position: todo.position, tagOrder: [...activeTags(todo)], tags: { ...(todo.tags || {}) } })),
        reducedMotion: capabilities.reducedMotion,
        hapticFeedback: capabilities.hapticFeedback,
        drop: async ({ droppedId, nextColumn, placements, original, destinationIndex }) => {
          const droppedTodo = capabilities.tasks().find(todo => todo.id === droppedId); if (!droppedTodo) return;
          const previousColumn = activeTags(droppedTodo)[0] || null; const wasCompleted = droppedTodo.completed; const completedAt = droppedTodo.completedAt;
          const changedPlacements = placements.filter(placement => placement.id === droppedId || capabilities.tasks().find(todo => todo.id === placement.id)?.position !== placement.position);
          await Promise.all(changedPlacements.map(placement => capabilities.mutate(placement.id, todo => {
            todo.position = placement.position; if (placement.id !== droppedId) return;
            if (placement.epicId !== undefined) todo.epicId = placement.epicId;
            const category = capabilities.categories().find(item => item.id === capabilities.categoryId());
            const transition = boardTagTransition(todo, category, placement.boardTag); todo.tagOrder = transition.tagOrder; todo.tags = transition.tags;
            const completed = transition.previousColumn === placement.boardTag ? todo.completed : transitionedCompletion(todo.completed, transition.previousColumn ? category?.boardColumnSettings?.[transition.previousColumn] : undefined, placement.boardTag ? category?.boardColumnSettings?.[placement.boardTag] : undefined);
            todo.completed = completed; todo.completedAt = completed ? todo.completedAt || capabilities.now() : null;
          })));
          const movedBeforeRender = await capabilities.loadTask(droppedId); if (!wasCompleted && movedBeforeRender?.completed) await capabilities.createNextOccurrence(movedBeforeRender);
          const categoryId = capabilities.categoryId();
          if (categoryId) capabilities.ensureVisible?.(categoryId, nextColumn || "Unassigned", destinationIndex);
          await capabilities.render(false); capabilities.highlightMoved?.(droppedId); const moved = capabilities.tasks().find(todo => todo.id === droppedId); const exceeded = previousColumn !== nextColumn && !!moved && capabilities.wipExceeded(moved);
          capabilities.actionUndo(`${nextColumn ? `Placed in ${nextColumn}` : "Placed in Unassigned"}${exceeded ? " · WIP limit exceeded" : ""}`, async () => {
            const changedIds = new Set(changedPlacements.map(placement => placement.id));
            await Promise.all(original.filter(snapshot => changedIds.has(snapshot.id)).map(snapshot => capabilities.mutate(snapshot.id, todo => { todo.epicId = snapshot.epicId || null; todo.position = snapshot.position; todo.tagOrder = [...snapshot.tagOrder]; todo.tags = { ...snapshot.tags }; })));
            await capabilities.mutate(droppedId, todo => { todo.completed = wasCompleted; todo.completedAt = completedAt; });
          });
          capabilities.sync();
        },
        move: async (ids, direction, reveal) => {
          const before = ids.map(id => capabilities.snapshot(capabilities.tasks().find(todo => todo.id === id)!));
          await Promise.all(ids.map((id, position) => capabilities.mutate(id, todo => { todo.position = position; })));
          if (reveal) capabilities.ensureVisible?.(reveal.collectionId, reveal.column, reveal.index);
          await capabilities.render(true); const rootId = capabilities.rootId(); if (rootId) capabilities.taskUndo(`Moved task ${direction < 0 ? "up" : "down"}`, rootId, before, ["position"] as Field[]); capabilities.sync();
        },
      });
    },
  };
}
